import { CELLS_PER_TILE, canStand, type CollisionGrid } from "../movement/step.ts";
import { feetInside, nearestStandable } from "../movement/nearest.ts";
import { MAX_RESPAWN_POINTS, RespawnPointKind } from "../protocol/respawn.ts";
import { compareShrineOrder, worldDistance, worldPosition, type KnownShrine, type WorldPlace, type RankedShrine } from "./world.ts";

/**
 * Where a dead character may stand up (ADR-096 decisions 14, 30 and 31): the shrines its character has discovered,
 * the nearest first, and the map's Spawn Point only when no shrine is offered (the amendment of 2026-10-14).
 * A shrine not discovered is never a point, so a new character is offered the spawn alone.
 * The rule is pure: the map server hands it its shrines, the character's set and its live grid at a death.
 * The offer on the wire (`respawnOffer`) is unchanged.
 */

/**
 * The most shrines a map holds: a character's discovered set on a map is one bit a shrine in two 32-bit words.
 */
export const MAX_SHRINES = 64;

/**
 * The most shrines an offer lists, leaving the spawn its place among `MAX_RESPAWN_POINTS`.
 */
export const MAX_OFFERED_SHRINES = MAX_RESPAWN_POINTS - 1;

/**
 * The `shrine` a point carries when it is the map's spawn.
 */
export const SPAWN_POINT_INDEX = -1;

/**
 * The `shrine` a point carries when it is on another map: the map server has no index for it, and `elsewhere` names it.
 */
export const ELSEWHERE_POINT_INDEX = -2;

/**
 * How far from a shrine's standing spot a spot that can be stood on is looked for, in tiles; past it the shrine is
 * buried and left out.
 */
export const SHRINE_STAND_RADIUS_TILES = 8;

const WORD_BITS = 32;

/**
 * A shrine as the rule reads it: its index on its map (0 to 63, its bit in a discovered set), its name (empty for
 * none), its ground point in sixteenths, which distance is measured to, and the feet point the search for a standing
 * spot starts from (`shrineStandFrom` for a thing, the point itself for a marker). A waypoint (ADR-137 decision 2)
 * holds a place in the set and is never a point to stand up at: `waypoint` is `true` for one and absent for a shrine.
 */
export interface ShrinePoint {
  readonly index: number;
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly standX: number;
  readonly standY: number;
  readonly waypoint?: true;
}

/**
 * A point a dead character may stand up at: its kind and name as the offer carries them, the feet point in sixteenths
 * it stands up at, and the index of the shrine it is, `SPAWN_POINT_INDEX` for the spawn, so the map server knows which
 * shrine an arrival pops.
 */
export interface ShrineRespawnPoint {
  readonly kind: (typeof RespawnPointKind)[keyof typeof RespawnPointKind];
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly shrine: number;
  readonly elsewhere?: { readonly mapId: string; readonly mapName: string; readonly shrineId: string };
}

/**
 * The home point as the gateway hands it to a map away from home: the home map's starting shrine, or its Spawn Point
 * when it starts players at one only, with the standing point in the home map's own sixteenths.
 */
export interface HomePoint {
  readonly kind: (typeof RespawnPointKind)[keyof typeof RespawnPointKind];
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly mapId: string;
  readonly mapName: string;
  readonly shrineId: string;
}

/**
 * What a death needs of the world (ADR-106 decision 13): this map's id and placement (null when unplaced), whether it
 * is the home map, the shrines the character knows on other maps, the home point, and the ids of the maps closed to
 * arrivals, whose shrines are left out and whose home point is not offered.
 */
export interface AcrossMaps {
  readonly mapId: string;
  readonly place: WorldPlace | null;
  readonly home: boolean;
  readonly known: readonly KnownShrine[];
  readonly homePoint: HomePoint | null;
  readonly closed: readonly string[];
}

/**
 * Whether a discovered set holds a shrine. Nothing is allocated.
 *
 * @param low The set's low word, shrines 0 to 31.
 * @param high The set's high word, shrines 32 to 63.
 * @param index The shrine's index.
 * @returns True when its bit is set; false for an index that is not a whole number from 0 to 63.
 */
export function shrineDiscovered(low: number, high: number, index: number): boolean {
  if (!Number.isInteger(index) || index < 0 || index >= MAX_SHRINES) {
    return false;
  }
  return index < WORD_BITS ? ((low >>> index) & 1) === 1 : ((high >>> (index - WORD_BITS)) & 1) === 1;
}

/**
 * Where a character stands up at a shrine or arrives at a place by travel (ADR-137 decision 17): its standing spot when a character can stand there on the live grid, else
 * the nearest spot that can be stood on within `SHRINE_STAND_RADIUS_TILES` of it.
 *
 * @param shrine The shrine.
 * @param live The live collision grid.
 * @returns The spot, or null for a buried shrine, which is never offered.
 */
export function shrineStandSpot(shrine: ShrinePoint, live: CollisionGrid): { readonly x: number; readonly y: number } | null {
  if (feetInside(shrine.standX, shrine.standY, live) && canStand(shrine.standX, shrine.standY, live)) {
    return { x: shrine.standX, y: shrine.standY };
  }
  return nearestStandable(shrine.standX, shrine.standY, live, SHRINE_STAND_RADIUS_TILES * CELLS_PER_TILE);
}

/**
 * The points a dead character is offered, the default first. A waypoint is never one.
 * With no `across` the map stands alone (ADR-096): its discovered shrines by squared distance in sixteenths from the
 * body's feet point to each ground point (ties to the lower y, then the lower x, then the lower index), a buried one
 * left out, at most `MAX_OFFERED_SHRINES` of them, then the map's spawn only when none of them was offered.
 * With `across` (ADR-106 decisions 13 and 14) every discovered shrine competes by `worldDistance` under the order of
 * decision 5. A shrine elsewhere is offered only when it is the nearest of all, and this map's others follow.
 * The Spawn Point is offered on the home map only, and only when no shrine is.
 * The home point stands alone when nothing is known, and nothing is offered when that point's map is closed.
 * An unplaced map is measured alone and ignores the known list.
 * Called once a death, a join that restores a dead character and a take-back, never per tick.
 *
 * @param shrines The map's shrines.
 * @param low The character's discovered set, low word.
 * @param high The character's discovered set, high word.
 * @param x The body's feet x in sixteenths.
 * @param y The body's feet y in sixteenths.
 * @param live The live collision grid.
 * @param spawn The map's spawn, feet point in sixteenths.
 * @param across The world, absent for a map that stands alone.
 * @returns The points, 0 to `MAX_RESPAWN_POINTS`; 1 or more unless `across` is given and the character must wait.
 */
export function respawnPoints(shrines: readonly ShrinePoint[], low: number, high: number, x: number, y: number, live: CollisionGrid, spawn: { readonly x: number; readonly y: number }, across?: AcrossMaps): ShrineRespawnPoint[] {
  if (across === undefined) {
    return aloneOffer(shrines, low, high, x, y, live, spawn);
  }
  const place = across.place;
  const body = place === null ? { x, y } : worldPosition(place, x, y);
  const own: { readonly shrine: ShrinePoint; readonly spot: { readonly x: number; readonly y: number }; readonly rank: RankedShrine }[] = [];
  for (const shrine of shrines) {
    if (shrine.waypoint === true || !shrineDiscovered(low, high, shrine.index)) {
      continue;
    }
    const spot = shrineStandSpot(shrine, live);
    if (spot === null) {
      continue;
    }
    const at = place === null ? { x: shrine.x, y: shrine.y } : worldPosition(place, shrine.x, shrine.y);
    own.push({ shrine, spot, rank: { distance: worldDistance(at.x, at.y, body.x, body.y), ownMap: true, worldX: at.x, worldY: at.y, mapId: across.mapId, shrineId: String(shrine.index).padStart(2, "0") } });
  }
  own.sort((a, b) => compareShrineOrder(a.rank, b.rank));
  let leader: { readonly known: KnownShrine; readonly rank: RankedShrine } | null = null;
  if (place !== null) {
    for (const known of across.known) {
      if (known.mapId === across.mapId || across.closed.includes(known.mapId)) {
        continue;
      }
      const rank: RankedShrine = { distance: worldDistance(known.x, known.y, body.x, body.y), ownMap: false, worldX: known.x, worldY: known.y, mapId: known.mapId, shrineId: known.shrineId };
      if (leader === null || compareShrineOrder(rank, leader.rank) < 0) {
        leader = { known, rank };
      }
    }
  }
  const points: ShrineRespawnPoint[] = [];
  if (leader !== null && (own.length === 0 || compareShrineOrder(leader.rank, (own[0] as { readonly rank: RankedShrine }).rank) < 0)) {
    const k = leader.known;
    points.push({ kind: RespawnPointKind.Shrine, name: k.name, x: k.standX, y: k.standY, shrine: ELSEWHERE_POINT_INDEX, elsewhere: { mapId: k.mapId, mapName: k.mapName, shrineId: k.shrineId } });
  }
  for (const entry of own) {
    if (points.length === MAX_OFFERED_SHRINES) {
      break;
    }
    points.push({ kind: RespawnPointKind.Shrine, name: entry.shrine.name, x: entry.spot.x, y: entry.spot.y, shrine: entry.shrine.index });
  }
  if (across.home) {
    if (points.length === 0) {
      points.push({ kind: RespawnPointKind.Spawn, name: "", x: spawn.x, y: spawn.y, shrine: SPAWN_POINT_INDEX });
    }
    return points;
  }
  if (points.length === 0 && across.homePoint !== null && !across.closed.includes(across.homePoint.mapId)) {
    const h = across.homePoint;
    points.push({ kind: h.kind, name: h.name, x: h.x, y: h.y, shrine: ELSEWHERE_POINT_INDEX, elsewhere: { mapId: h.mapId, mapName: h.mapName, shrineId: h.shrineId } });
  }
  return points;
}

/**
 * The offer of a map that stands alone: its own discovered shrines by local distance, then the spawn.
 *
 * @param shrines The map's shrines.
 * @param low The character's discovered set, low word.
 * @param high The character's discovered set, high word.
 * @param x The body's feet x in sixteenths.
 * @param y The body's feet y in sixteenths.
 * @param live The live collision grid.
 * @param spawn The map's spawn.
 * @returns The points, 1 to `MAX_OFFERED_SHRINES` shrines, or the spawn alone when no shrine can be offered.
 */
function aloneOffer(shrines: readonly ShrinePoint[], low: number, high: number, x: number, y: number, live: CollisionGrid, spawn: { readonly x: number; readonly y: number }): ShrineRespawnPoint[] {
  const known = shrines
    .filter((shrine) => shrine.waypoint !== true && shrineDiscovered(low, high, shrine.index))
    .map((shrine) => ({ shrine, distance: (shrine.x - x) * (shrine.x - x) + (shrine.y - y) * (shrine.y - y) }))
    .sort((a, b) => a.distance - b.distance || a.shrine.y - b.shrine.y || a.shrine.x - b.shrine.x || a.shrine.index - b.shrine.index);
  const points: ShrineRespawnPoint[] = [];
  for (const { shrine } of known) {
    if (points.length === MAX_OFFERED_SHRINES) {
      break;
    }
    const spot = shrineStandSpot(shrine, live);
    if (spot !== null) {
      points.push({ kind: RespawnPointKind.Shrine, name: shrine.name, x: spot.x, y: spot.y, shrine: shrine.index });
    }
  }
  if (points.length === 0) {
    points.push({ kind: RespawnPointKind.Spawn, name: "", x: spawn.x, y: spawn.y, shrine: SPAWN_POINT_INDEX });
  }
  return points;
}
